import React, { useContext, useEffect, useState, useMemo } from "react";
import { Box, Typography, Card, CardContent, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { PatientContext } from "../../../../../context/PatientContext/PatientContext";
import { AdvancedMdContext } from "../../../../../context/AdvancedMD/AdvancedMdContext";
import { setAdvancedMdPatientId } from "../../../../../store/Profiles/PatientMapSelectSlice";

import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import HealingIcon from "@mui/icons-material/Healing";
import AirlineSeatFlatAngledIcon from "@mui/icons-material/AirlineSeatFlatAngled";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import EmergencyIcon from "@mui/icons-material/ReportProblem";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncIcon from "@mui/icons-material/Sync";

export default function PatientInformation({ patientId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { patientDetails, loading, error, fetchPatientDetails } = useContext(PatientContext);
  const {
    matchPatientInAdvancedMd,
    patientLookupResults,
    lookupLoading,
    lookupError,
  } = useContext(AdvancedMdContext);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    if (patientId) {
      fetchPatientDetails(patientId);
    } else {
      setPatient(null);
    }
  }, [patientId, fetchPatientDetails]);

  useEffect(() => {
    if (patientDetails  && patientDetails.length > 0) {
      const p = patientDetails[0];
      setPatient({
        fullName: p.memberFullName,
        firstName: p.firstName || "",
        lastName: p.lastName || "",
        memberId: p.memberId,
        cipId: p.varCipId,
        dob: p.dateOfBirth?.split("T")[0],
        age: p.age,
        gender: p.gender === "M" ? "Male" : "Female",
        genderCode: p.gender || "",
        email: p.memberEmail || "Not available",
        phone: p.memberPhone || "Not available",
        mobile: p.memberMobile || "Not available",
        status: p.status || "Unknown",
        role: p.role ?? "N/A",
        careManager: p.careManager ?? "N/A",
        language: p.language ?? "N/A",
      });
    } else {
      setPatient(null);
    }
  }, [patientDetails]);

  // ===================== AdvancedMD match =====================
  const matchPayload = useMemo(() => {
    if (!patient) return null;

    const firstName = patient.firstName || "";
    const lastName = patient.lastName || "";
    const dob = patient.dob || null;
    const sex = patient.genderCode || "";

    if (!firstName && !lastName && !patient.fullName) return null;

    let dobIso = null;
    if (dob) {
      const d = new Date(dob);
      if (!Number.isNaN(d.getTime())) dobIso = d.toISOString();
    }

    return { firstName, lastName, dob: dobIso, sex };
  }, [patient]);

  useEffect(() => {
    if (!matchPayload) return;
    matchPatientInAdvancedMd(matchPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchPayload]);

  const bestMatch = useMemo(
    () => (patientLookupResults && patientLookupResults.length > 0 ? patientLookupResults[0] : null),
    [patientLookupResults]
  );

  const candidate = bestMatch?.candidate ?? null;

  const chartValue =
    candidate &&
    (candidate.chart ||
      candidate.Chart ||
      candidate.chartId ||
      candidate.ChartId ||
      candidate.ChartID ||
      candidate.chart_id);

  useEffect(() => {
    if (!candidate?.id) return;
    const rawId = String(candidate.id);
    const normalizedId = rawId.startsWith("pat") ? rawId.slice(3) : rawId;
    dispatch(setAdvancedMdPatientId(normalizedId));
  }, [candidate, dispatch]);

  if (loading) {
    return <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>Loading...</Typography>;
  }

  if (error) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 2, color: "red" }}>
        Error: {error.message}
      </Typography>
    );
  }

  if (!patient) {
    return (
      <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>
        {t("CarePlan.patientInfo2.notFound")}
      </Typography>
    );
  }

  const Field = ({ label, value }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  );

  return (
    <Card sx={{ width: "100%", boxShadow: 3, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#085394", mb: 2 }}>
          {t("CarePlan.patientInfo2.title")}
        </Typography>

        {/* Información general */}
        <Field label="CIP ID" value={patient.cipId} />
        <Field label={t("CarePlan.patientInfo2.memberId")} value={patient.memberId} />
        <Field label={t("CarePlan.patientInfo2.fullName")} value={patient.fullName} />
        <Field label={t("CarePlan.patientInfo2.gender")} value={patient.gender} />
        <Field label={t("CarePlan.patientInfo2.dob")} value={patient.dob} />
        <Field label={t("CarePlan.patientInfo2.age")} value={patient.age} />
        <Field label={t("CarePlan.patientInfo2.status")} value={patient.status} />

        {/* Contacto */}
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#085394", mt: 3, mb: 1 }}>
          {t("CarePlan.patientInfo2.contact")}
        </Typography>
        <Field label={t("CarePlan.patientInfo2.email")} value={patient.email} />
        <Field label={t("CarePlan.patientInfo2.phone")} value={patient.phone} />
        <Field label="Mobile" value={patient.mobile} />

        {/* Equipo de Cuidado */}
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#085394", mt: 3, mb: 1 }}>
          {t("CarePlan.patientInfo2.careTeam")}
        </Typography>
        <Field label={t("CarePlan.patientInfo2.careManager")} value={patient.careManager} />
        <Field label={t("CarePlan.patientInfo2.language")} value={patient.language} />
        <Field label={t("CarePlan.patientInfo2.role")} value={patient.role} />

        {/* AdvancedMD Match */}
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#085394", mt: 3, mb: 1 }}>
          AdvancedMD
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          {lookupLoading && (
            <Chip icon={<SyncIcon />} label="Matching..." color="info" size="small" variant="outlined" />
          )}
          {!lookupLoading && lookupError && (
            <Chip icon={<ErrorOutlineIcon />} label="Error" color="error" size="small" variant="outlined" />
          )}
          {!lookupLoading && !lookupError && candidate && (
            <Chip icon={<CheckCircleIcon />} label="Matched" color="success" size="small" variant="filled" />
          )}
          {!lookupLoading && !lookupError && !candidate && patient && matchPayload && (
            <Chip icon={<ErrorOutlineIcon />} label="No match" color="warning" size="small" variant="outlined" />
          )}
        </Box>
        {chartValue && <Field label="AdvancedMD Chart" value={chartValue} />}

        {/* Información Clínica */}
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#085394", mt: 3, mb: 1 }}>
          {t("CarePlan.patientInfo2.clinicalInfo")}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {patient.highRisk && (
            <Chip
              icon={<WarningAmberIcon />}
              label={t("CarePlan.patientInfo2.highRisk")}
              color="warning"
              variant="filled"
            />
          )}
          {patient.highCost && (
            <Chip
              icon={<HealingIcon />}
              label={t("CarePlan.patientInfo2.highCost")}
              color="secondary"
              variant="filled"
            />
          )}
          {patient.discharged && (
            <Chip
              icon={<AirlineSeatFlatAngledIcon />}
              label={t("CarePlan.patientInfo2.discharged")}
              color="default"
              variant="outlined"
            />
          )}
          {patient.admitted && (
            <Chip
              icon={<LocalHospitalIcon />}
              label={t("CarePlan.patientInfo2.admitted")}
              color="success"
              variant="filled"
            />
          )}
          {patient.emergency && (
            <Chip
              icon={<EmergencyIcon />}
              label={t("CarePlan.patientInfo2.emergency")}
              color="error"
              variant="filled"
            />
          )}
        </Box>

      </CardContent>
    </Card>
  );
}
